import { HttpContextToken, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { TokenService } from '../services/token.service';
import { SessionManagerService } from '../services/session-manager.service';
import { environment } from '../../../environments/environment';
import { catchError, switchMap, throwError, from, EMPTY } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Logger } from '../../shared/utils/logger.util';

/**
 * When set on a request's HttpContext, this token is used as the Bearer token
 * instead of the ICS access token from TokenService. Allows MBC (and other
 * non-ICS) requests to carry their own auth token through the shared interceptor.
 */
export const BEARER_TOKEN_OVERRIDE = new HttpContextToken<string | null>(() => null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const injector = inject(Injector);
  const tokenOverride = req.context.get(BEARER_TOKEN_OVERRIDE);
  const accessToken = tokenOverride ?? tokenService.getAccessTokenValue();

  let authReq = req;

  // Handle API requests
  // Check if URL is an API request (either matches apiUrl or is a relative /api path when using proxy)
  const isApiRequest = environment.apiUrl
    ? req.url.startsWith(environment.apiUrl)
    : req.url.includes('/api/');

  if (isApiRequest) {
    const headers: { [key: string]: string } = {};

    // Add access token ONLY for non-auth endpoints
    if (accessToken && !req.url.includes('/auth/')) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    authReq = req.clone({
      setHeaders: headers,
      // withCredentials is handled by credentialsInterceptor
    });
  }

  // Early return for auth endpoints AFTER setting headers
  if (req.url.includes('/auth/')) {
    return next(authReq);
  }

  return next(authReq).pipe(
    tap((event: any) => {
      // Check for auto-refreshed token in response headers (legacy support)
      if (event.headers && event.headers.has('X-New-Access-Token')) {
        const newToken = event.headers.get('X-New-Access-Token');
        if (newToken) {
          tokenService.setAccessToken(newToken);
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Handle 401 unauthorized errors for non-auth endpoints
      const isApiError = environment.apiUrl 
        ? req.url.startsWith(environment.apiUrl)
        : req.url.includes('/api/');
      
      if (error.status === 401 &&
          isApiError &&
          !req.url.includes('/auth/') &&
          tokenService.isAuthenticatedValue()) {

        Logger.log('401 error detected, delegating to session manager...');

        // Snapshot the token at 401-time.  If a background refresh rotated it
        // while the session manager was rate-limited, we detect the change and
        // retry instead of silently dropping the request with EMPTY.
        const tokenAtError = tokenService.getAccessTokenValue();

        // Lazy-resolve to break SessionManagerService ↔ AuthStore circular DI during bootstrap
        const sessionManager = injector.get(SessionManagerService);
        return from(sessionManager.handleUnauthorizedRequest()).pipe(
          switchMap((refreshed) => {
            const currentToken = tokenService.getAccessTokenValue();
            if (refreshed) {
              Logger.log('Session manager refreshed token successfully, retrying request');
              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${currentToken}` },
              });
              return next(retryReq);
            } else if (currentToken && currentToken !== tokenAtError) {
              // Background refresh rotated the token while the session manager
              // was rate-limited.  The new token is fresh — retry.
              Logger.log('Background refresh rotated token during 401 handling, retrying with new token');
              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${currentToken}` },
              });
              return next(retryReq);
            } else {
              Logger.log('Session manager could not refresh token, returning original error');
              // Session manager handled the failure (including logout redirect)
              return EMPTY; // Don't return error to prevent duplicate error handling
            }
          }),
          catchError((refreshError) => {
            Logger.log('Error during session manager refresh:', refreshError);
            return EMPTY; // Let session manager handle the error
          })
        );
      }

      // For non-401 errors or auth endpoints, just pass through
      return throwError(() => error);
    })
  );
};